// Libs
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Store & actions
import actionsUsers from '@/app/modules/users/monetic/api/actionsUsers';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import PasswordValidation from '@/common-utils/components/renders/passwordValidation/PasswordValidation';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel
} from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import { usePasswordValidation } from '@/common-utils/hooks/usePasswordValidation';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsSession } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @returns {JSX} ModalUpdatePassword
 */
function ModalUpdatePassword({ isOpen, setIsOpen }) {

    // Toast
    const { toast } = useToast();

    // Lang
    const { t: translate, i18n } = useTranslation();

    // Password Validation
    const {
        passwordSchema,
        status: newPasswordStatus,
        validate: validatePassword
    } = usePasswordValidation({
        translate: translate,
        language: i18n.language,
        minLength: 12
    });

    // Form
    const formSchema = z.object({
        currentPassword: z.string().min(12),
        newPassword: passwordSchema,
        confirmPassword: z.string()
    }).refine((data) => data.newPassword === data.confirmPassword, {
        message: '',
        path: ['confirmPassword']
    });

    // Form
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        }
    });

    const newPassword = form.watch('newPassword');
    const confirmPassword = form.watch('confirmPassword');

    const passwordsTyped = newPassword && confirmPassword;
    const passwordsMatch = passwordsTyped && newPassword === confirmPassword;
    const passwordsMismatch = passwordsTyped && !passwordsMatch;

    // Reset form
    const resetForm = () => {
        // Reset form
        form.reset();
        // Reset password validation
        validatePassword('');
    };

    // API call
    const onSuccess = () => {
        resetForm();
        setIsOpen(false);
        toast({
            description: <Entity namespace={nsSession} entity='update_password.success' />,
            variant: 'success'
        });
    };

    const onError = (error) => {
        resetForm();
        setIsOpen(false);
        toast({
            description: <Entity entity={error.message} />,
            variant: 'danger'
        });
    };

    const submitUpdatePassword = actionsUsers.updatePassword(onSuccess, onError);

    // Submit
    const onSubmit = (values) => {
        const { currentPassword, newPassword: newPasswordValue } = values;

        submitUpdatePassword.mutate({
            oldPassword: currentPassword,
            newPwd: newPasswordValue
        });
    };

    // Functions
    const confirmPasswordsClassName = () => {
        let className = 'italic text-sm font-extralight text-black';
        if (passwordsTyped) {
            if (passwordsMatch) { className = 'italic text-sm font-medium text-success-foreground'; }
            else { className = 'italic text-sm font-medium text-danger-foreground'; }
        }
        return className;
    };

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-lg text-black'>
                <HeaderTitle
                    title='update_password.title'
                    namespace={nsSession}
                />
                <ModalBody>
                    <Form {...form}>
                        <form className='flex flex-col [&>*]:mt-4' onSubmit={form.handleSubmit(onSubmit)}>

                            {/* Current password */}
                            <FormField
                                control={form.control}
                                name='currentPassword'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>
                                            <Entity namespace={nsSession} entity='update_password.current_password' />
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type='password'
                                                autoComplete='off'
                                                {...field}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='newPassword'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>
                                            <Entity namespace={nsSession} entity='update_password.new_password' />
                                        </FormLabel>

                                        <PasswordValidation validationStatus={newPasswordStatus} />

                                        <FormControl>
                                            <Input
                                                type='password'
                                                value={field.value}
                                                autoComplete='off'
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    validatePassword(value);
                                                    field.onChange(value);
                                                }}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='confirmPassword'
                                render={({ field }) => {
                                    let validationEntity = '';

                                    if (passwordsMatch) {
                                        validationEntity = 'update_password.passwords_match';
                                    } else if (passwordsMismatch) {
                                        validationEntity = 'update_password.passwords_mismatch';
                                    }

                                    return (
                                        <FormItem>
                                            <FormLabel required>
                                                <Entity namespace={nsSession} entity='update_password.confirm_password' />
                                            </FormLabel>

                                            <FormControl>
                                                <Input
                                                    type='password'
                                                    autoComplete='off'
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                />
                                            </FormControl>

                                            <Entity
                                                className={confirmPasswordsClassName()}
                                                namespace={nsSession}
                                                entity={validationEntity}
                                            />
                                        </FormItem>
                                    );
                                }}
                            />
                        </form>
                    </Form>
                </ModalBody>
                <FooterForm
                    onReset={resetForm}
                    onSubmit={form.handleSubmit(onSubmit)}
                    isSubmitDisabled={!form.formState.isValid}
                    isSubmitting={submitUpdatePassword.isLoading}
                    showSubmitButton
                />
            </ModalContent>
        </Modal>
    );
}

ModalUpdatePassword.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired
};

export default ModalUpdatePassword;